import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
  VisibilityState,
  RowPinningState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Row,
  type Table as ReactTable,
} from '@tanstack/react-table';

import { TABLE_PAGINATION } from '../../../util/constants';

interface TableContextProps<TData> {
  table: ReactTable<TData>;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  currentPageIndex?: number;
  setCurrentPageIndex?: (index: number) => void;
  rowCount?: number;
  onRowClick?: (row: TData) => void;
  onRowHover?: (row: TData) => void;
  getRowClickable?: (row: TData) => boolean;
  getRowHref?: (row: TData) => string | null;
  getRowLinkState?: (row: TData) => object | undefined;
  resourceName?: string;
  isFetchingNextPage?: boolean;
  emptyStateMessage?: React.ReactNode;
  infiniteContentClassName?: string;
  isLoading?: boolean;
  enableRowPinning?: boolean;
  keepPinnedRows?: boolean;
  onRowPinningChange?: (updater: (prev: RowPinningState) => RowPinningState) => void;
  rowPinning?: RowPinningState;
  enableRowHover?: boolean;
  activeRowId?: string;
  containerClassName?: string;
}

// Using any here is intentional as we need a placeholder for the generic type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TableContext = createContext<TableContextProps<any> | undefined>(undefined);

export const useTableContext = <TData,>(): TableContextProps<TData> => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context as TableContextProps<TData>;
};

interface TableProviderProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  children: React.ReactNode;
  pagination?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  currentPageIndex?: number;
  setCurrentPageIndex?: (index: number) => void;
  initialPageIndex?: number;
  onPageChange?: (pageIndex: number) => void;
  rowCount?: number;
  onRowClick?: (row: TData) => void;
  onRowHover?: (row: TData) => void;
  getRowClickable?: (row: TData) => boolean;
  getRowHref?: (row: TData) => string | null;
  getRowLinkState?: (row: TData) => object | undefined;
  resourceName?: string;
  isFetchingNextPage?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  emptyStateMessage?: React.ReactNode;
  infiniteContentClassName?: string;
  isLoading?: boolean;
  enableRowPinning?: boolean;
  keepPinnedRows?: boolean;
  onRowPinningChange?: (updater: (prev: RowPinningState) => RowPinningState) => void;
  rowPinning?: RowPinningState;
  getRowId?: (row: TData) => string;
  enableRowHover?: boolean;
  activeRowId?: string;
  autoResetPageIndex?: boolean;
  containerClassName?: string;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
}

export const TableProvider = <TData, TValue>({
  columns,
  data,
  children,
  pagination,
  fetchNextPage,
  hasNextPage,
  currentPageIndex,
  setCurrentPageIndex,
  initialPageIndex,
  onPageChange,
  rowCount,
  onRowClick,
  onRowHover,
  getRowClickable,
  getRowHref,
  getRowLinkState,
  resourceName,
  isFetchingNextPage,
  columnVisibility: externalColumnVisibility,
  onColumnVisibilityChange: externalOnColumnVisibilityChange,
  sorting: externalSorting,
  onSortingChange: externalOnSortingChange,
  manualSorting,
  emptyStateMessage,
  infiniteContentClassName,
  isLoading,
  enableRowPinning,
  keepPinnedRows,
  onRowPinningChange,
  rowPinning,
  getRowId,
  enableRowHover,
  activeRowId,
  autoResetPageIndex = false,
  containerClassName,
  enableRowSelection = true,
}: TableProviderProps<TData, TValue>) => {
  const [rowSelection, setRowSelection] = useState({});
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  // Use external visibility state if provided, otherwise use internal state
  const columnVisibility = externalColumnVisibility ?? internalColumnVisibility;
  const onColumnVisibilityChange = externalOnColumnVisibilityChange ?? setInternalColumnVisibility;

  // Use external sorting state if provided, otherwise use internal state
  const sorting = externalSorting ?? internalSorting;
  const onSortingChange = externalOnSortingChange ?? setInternalSorting;

  // The React Compiler skips this component by itself. The table components it feeds are
  // excluded in vite.config.ts.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    manualPagination: pagination ? true : false,
    rowCount: pagination ? rowCount : undefined,
    getRowId: getRowId,
    initialState: { pagination: { pageIndex: initialPageIndex ?? 0, pageSize: TABLE_PAGINATION } },
    state: {
      columnVisibility,
      rowSelection,
      columnFilters,
      sorting,
      rowPinning: rowPinning || { top: [], bottom: [] },
      ...(pagination && {
        pagination: { pageIndex: currentPageIndex || 0, pageSize: TABLE_PAGINATION },
      }),
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange,
    onSortingChange,
    onRowPinningChange: onRowPinningChange,
    enableRowPinning: enableRowPinning,
    keepPinnedRows: keepPinnedRows,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualSorting,
    // Use getPaginationRowModel for client-side pagination (when not using manual/server pagination)
    ...(!pagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    ...(!manualSorting ? { getSortedRowModel: getSortedRowModel() } : {}),
  });

  // Reset page to 0 when data changes (opt-in via autoResetPageIndex prop).
  // We handle this ourselves instead of using TanStack's built-in autoResetPageIndex
  // because TanStack resets to initialState (the page from the URL on mount),
  // not to 0. For filter/search resets, page 0 is the correct target.
  // The first data change (initial load) is skipped to preserve the URL-restored page.
  const prevDataRef = useRef(data);
  const isFirstDataChange = useRef(true);
  useEffect(() => {
    if (prevDataRef.current === data) {
      return;
    }
    prevDataRef.current = data;

    if (isFirstDataChange.current) {
      isFirstDataChange.current = false;
      return;
    }

    if (autoResetPageIndex) {
      table.setPageIndex(0);
    }
  }, [autoResetPageIndex, data, table]);

  // Track page changes and call onPageChange callback
  const prevPageRef = useRef(initialPageIndex ?? 0);
  useEffect(() => {
    const currentPage = table.getState().pagination.pageIndex;
    if (onPageChange && currentPage !== prevPageRef.current) {
      prevPageRef.current = currentPage;
      onPageChange(currentPage);
    }
  }, [table.getState().pagination.pageIndex, onPageChange]);

  return (
    <TableContext.Provider
      value={
        {
          table,
          fetchNextPage,
          hasNextPage,
          currentPageIndex,
          setCurrentPageIndex,
          rowCount,
          onRowClick,
          onRowHover,
          getRowClickable,
          getRowHref,
          getRowLinkState,
          resourceName,
          isFetchingNextPage,
          emptyStateMessage,
          infiniteContentClassName,
          isLoading,
          enableRowPinning,
          keepPinnedRows,
          onRowPinningChange,
          rowPinning: rowPinning || { top: [], bottom: [] },
          enableRowHover,
          activeRowId,
          containerClassName,
        } as TableContextProps<TData>
      }
    >
      {children}
    </TableContext.Provider>
  );
};
